import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Text,
  VStack,
  HStack,
  Icon,
  Flex,
  Spinner,
  Button,
  useColorModeValue,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Badge,
  Progress,
  Tooltip,
  Grid,
  GridItem,
  CircularProgress,
  CircularProgressLabel,
} from '@chakra-ui/react';
import { 
  FaBullseye, 
  FaChartLine,
  FaStar,
  FaSmile,
  FaTrophy,
  FaFireAlt,
  FaChartBar,
  FaMedal,
  FaArrowUp,
  FaArrowDown,
  FaCrown,
  FaCheckCircle,
  FaChevronRight,
  FaGlobe,
  FaSearch,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { ResponsiveContainer, AreaChart, Area } from 'recharts';
import { getYearData } from '../services/yearService';
import { getReviews } from '../services/reviewService';
import { logger } from '../utils/logger';
import { motion } from 'framer-motion';
import { getTrafficValue, formatNumber, calculateSeoScore } from '../utils/competitiveIntelHelpers';

const MotionBox = motion(Box);

// Discrete slider positions - defined outside component to prevent re-renders
const SLIDER_POSITIONS = [1, 3, 5, 10, 15, 20];

/**
 * GoalsWidget - Premium goals widget with rich metrics, visual progress indicators,
 * and detailed market benchmarks
 */
const GoalsWidget = () => {
  const navigate = useNavigate();
  const [yearData, setYearData] = useState([]);
  const [reviewsData, setReviewsData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [targetPosition, setTargetPosition] = useState(5);
  const [hoveredMetric, setHoveredMetric] = useState(null);

  // Color mode values
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'white');
  const subtitleColor = useColorModeValue('gray.500', 'gray.400');
  const sliderTrackBg = useColorModeValue('gray.100', 'gray.600');
  const hoverBorderColor = useColorModeValue('blue.200', 'blue.600');
  const metricBg = useColorModeValue('gray.50', 'gray.700');
  const metricHoverBg = useColorModeValue('blue.50', 'gray.600');
  const headerBg = useColorModeValue('gray.50', 'gray.900');
  const iconBg = useColorModeValue('gray.100', 'gray.800');
  const glowColor = useColorModeValue('rgba(102, 126, 234, 0.3)', 'rgba(102, 126, 234, 0.2)');
  const ctaBg = useColorModeValue('purple.50', 'purple.900');
  const ctaTextColor = useColorModeValue('purple.700', 'purple.200');

  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      try {
        const [yearResult, reviewsResult] = await Promise.all([
          getYearData(),
          getReviews(),
        ]);
        
        if (mounted) {
          setYearData(yearResult || []);
          setReviewsData(reviewsResult || []);
          setIsLoading(false);
        }
      } catch (error) {
        logger.error('Error fetching data for Goals Widget:', error);
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      mounted = false;
    };
  }, []);

  // Helper to calculate avg reviews per month
  const calculateAvgMonthlyReviews = useCallback((business) => {
    const counts = Object.values(business?.monthlyReviewCounts || business?.historicalData?.monthlyReviewCounts || business?.data?.monthlyReviewCounts || {});
    if (!counts.length) return 0;
    return counts.reduce((a, b) => a + b, 0) / counts.length;
  }, []);

  // Helper to calculate sentiment score
  const calculateSentimentScore = useCallback((breakdown) => {
    if (!breakdown) return 0;
    const { positive = 0, neutral = 0, negative = 0 } = breakdown;
    const total = positive + neutral + negative;
    return total > 0 ? ((positive + neutral * 0.5) / total) * 100 : 0;
  }, []);

  // Combine reviews with year data
  const combinedData = useMemo(() => {
    if (!reviewsData.length) return [];
    const reviews = Array.isArray(reviewsData) ? reviewsData : reviewsData.reviews || [];
    return reviews.map(business => {
      const yearInfo = yearData.find(y => y.maps_uri === business.maps_uri);
      return {
        ...business,
        historicalData: yearInfo?.data || null,
        monthlyReviewCounts: yearInfo?.data?.monthlyReviewCounts || {},
        sentimentBreakdown: yearInfo?.data?.sentimentBreakdown || null,
      };
    });
  }, [reviewsData, yearData]);

  // Calculate independent rankings for each metric
  const totalReviewsRankings = useMemo(() => {
    if (!combinedData.length) return [];
    return [...combinedData]
      .filter(b => b.rating_count > 0)
      .sort((a, b) => b.rating_count - a.rating_count);
  }, [combinedData]);

  const monthlyReviewsRankings = useMemo(() => {
    if (!combinedData.length) return [];
    return [...combinedData]
      .map(b => ({
        ...b,
        avgMonthlyReviews: calculateAvgMonthlyReviews(b),
      }))
      .filter(b => b.avgMonthlyReviews > 0)
      .sort((a, b) => b.avgMonthlyReviews - a.avgMonthlyReviews);
  }, [combinedData, calculateAvgMonthlyReviews]);

  const sentimentRankings = useMemo(() => {
    if (!combinedData.length) return [];
    return [...combinedData]
      .map(b => ({
        ...b,
        sentimentScore: calculateSentimentScore(b.sentimentBreakdown),
      }))
      .filter(b => b.sentimentScore > 0)
      .sort((a, b) => b.sentimentScore - a.sentimentScore);
  }, [combinedData, calculateSentimentScore]);

  const trafficRankings = useMemo(() => {
    if (!combinedData.length) return [];
    return [...combinedData]
      .map(b => ({
        ...b,
        trafficValue: getTrafficValue(b),
      }))
      .filter(b => b.trafficValue !== null && b.trafficValue > 0)
      .sort((a, b) => b.trafficValue - a.trafficValue);
  }, [combinedData]);

  const seoScoreRankings = useMemo(() => {
    if (!combinedData.length) return [];
    return [...combinedData]
      .map(b => ({
        ...b,
        seoScoreValue: calculateSeoScore(b),
      }))
      .filter(b => b.seoScoreValue > 0)
      .sort((a, b) => b.seoScoreValue - a.seoScoreValue);
  }, [combinedData]);

  // Calculate market averages
  const marketStats = useMemo(() => {
    if (!combinedData.length) return null;
    
    const totalReviews = combinedData.reduce((sum, b) => sum + (b.rating_count || 0), 0);
    const avgReviews = Math.round(totalReviews / combinedData.length);
    
    const monthlyReviews = monthlyReviewsRankings.map(b => b.avgMonthlyReviews);
    const avgMonthly = monthlyReviews.length ? (monthlyReviews.reduce((a, b) => a + b, 0) / monthlyReviews.length).toFixed(1) : 0;
    
    const sentiments = sentimentRankings.map(b => b.sentimentScore);
    const avgSentiment = sentiments.length ? Math.round(sentiments.reduce((a, b) => a + b, 0) / sentiments.length) : 0;

    const trafficValues = trafficRankings.map(b => b.trafficValue);
    const avgTraffic = trafficValues.length 
      ? Math.round(trafficValues.reduce((a, b) => a + b, 0) / trafficValues.length) 
      : 0;

    const seoScores = seoScoreRankings.map(b => b.seoScoreValue);
    const avgSeoScore = seoScores.length 
      ? Math.round((seoScores.reduce((a, b) => a + b, 0) / seoScores.length) * 10) / 10
      : 0;
    
    return {
      avgReviews,
      avgMonthly: parseFloat(avgMonthly),
      avgSentiment,
      avgTraffic,
      avgSeoScore,
      totalBusinesses: combinedData.length,
      businessesWithTraffic: trafficRankings.length,
      businessesWithSeoScore: seoScoreRankings.length,
    };
  }, [combinedData, monthlyReviewsRankings, sentimentRankings, trafficRankings, seoScoreRankings]);

  // Calculate target thresholds based on slider position
  const targetThresholds = useMemo(() => {
    if (!totalReviewsRankings.length) return null;

    const effectiveMonthlyTarget = Math.min(targetPosition, monthlyReviewsRankings.length);
    const monthlyTarget = effectiveMonthlyTarget > 0
      ? Math.round(monthlyReviewsRankings[effectiveMonthlyTarget - 1]?.avgMonthlyReviews * 10) / 10
      : 0;

    const effectiveTotalTarget = Math.min(targetPosition, totalReviewsRankings.length);
    const totalReviewsTarget = effectiveTotalTarget > 0
      ? totalReviewsRankings[effectiveTotalTarget - 1]?.rating_count || 0
      : 0;

    const effectiveSentimentTarget = Math.min(targetPosition, sentimentRankings.length);
    const sentimentTarget = effectiveSentimentTarget > 0
      ? Math.round(sentimentRankings[effectiveSentimentTarget - 1]?.sentimentScore)
      : 85;

    // Traffic target
    const effectiveTrafficTarget = Math.min(targetPosition, trafficRankings.length);
    const trafficTarget = effectiveTrafficTarget > 0
      ? trafficRankings[effectiveTrafficTarget - 1]?.trafficValue || 0
      : 0;

    // SEO Score target
    const effectiveSeoTarget = Math.min(targetPosition, seoScoreRankings.length);
    const seoScoreTarget = effectiveSeoTarget > 0
      ? Math.round((seoScoreRankings[effectiveSeoTarget - 1]?.seoScoreValue || 0) * 10) / 10
      : 0;

    // Get the top business names for each metric
    const topReviewsBusiness = totalReviewsRankings[0]?.name || 'Leader';
    const topMonthlyBusiness = monthlyReviewsRankings[0]?.name || 'Leader';
    const topSentimentBusiness = sentimentRankings[0]?.name || 'Leader';
    const topTrafficBusiness = trafficRankings[0]?.name || 'Leader';
    const topSeoBusiness = seoScoreRankings[0]?.name || 'Leader';

    return {
      monthlyReviews: monthlyTarget,
      totalReviews: totalReviewsTarget,
      sentimentScore: sentimentTarget,
      trafficTarget: trafficTarget,
      seoScoreTarget: seoScoreTarget,
      topReviewsBusiness,
      topMonthlyBusiness,
      topSentimentBusiness,
      topTrafficBusiness,
      topSeoBusiness,
      marketLeader: totalReviewsRankings[0]?.rating_count || 0,
      monthlyLeader: monthlyReviewsRankings[0]?.avgMonthlyReviews || 0,
      sentimentLeader: sentimentRankings[0]?.sentimentScore || 0,
      trafficLeader: trafficRankings[0]?.trafficValue || 0,
      seoScoreLeader: seoScoreRankings[0]?.seoScoreValue || 0,
    };
  }, [totalReviewsRankings, monthlyReviewsRankings, sentimentRankings, trafficRankings, seoScoreRankings, targetPosition]);

  // Generate trend data for sparklines
  const trendData = useMemo(() => {
    if (!yearData.length) return [];
    
    // Aggregate monthly data across all businesses
    const monthlyTotals = {};
    yearData.forEach(business => {
      const counts = business?.data?.monthlyReviewCounts || {};
      Object.entries(counts).forEach(([month, count]) => {
        monthlyTotals[month] = (monthlyTotals[month] || 0) + count;
      });
    });
    
    return Object.entries(monthlyTotals)
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-6)
      .map(([month, total]) => ({ month, value: total }));
  }, [yearData]);

  // Use constant slider positions
  const currentIndex = SLIDER_POSITIONS.indexOf(targetPosition);
  const sliderIndex = currentIndex >= 0 ? currentIndex : 0;

  const handleSliderChange = useCallback((index) => {
    setTargetPosition(SLIDER_POSITIONS[index]);
  }, []);

  const handleViewGoals = useCallback(() => {
    navigate('/playbook/goals');
  }, [navigate]);

  // Calculate overall goal progress (average of all metrics vs targets)
  const overallProgress = useMemo(() => {
    if (!marketStats || !targetThresholds) return 0;
    
    const reviewProgress = Math.min((marketStats.avgReviews / targetThresholds.totalReviews) * 100, 100) || 0;
    const monthlyProgress = Math.min((marketStats.avgMonthly / targetThresholds.monthlyReviews) * 100, 100) || 0;
    const sentimentProgress = Math.min((marketStats.avgSentiment / targetThresholds.sentimentScore) * 100, 100) || 0;
    const trafficProgress = targetThresholds.trafficTarget > 0 
      ? Math.min((marketStats.avgTraffic / targetThresholds.trafficTarget) * 100, 100) 
      : 0;
    const seoProgress = targetThresholds.seoScoreTarget > 0
      ? Math.min((marketStats.avgSeoScore / targetThresholds.seoScoreTarget) * 100, 100)
      : 0;

    const metrics = [reviewProgress, monthlyProgress, sentimentProgress];
    if (trafficProgress > 0) metrics.push(trafficProgress);
    if (seoProgress > 0) metrics.push(seoProgress);
    
    return Math.round(metrics.reduce((a, b) => a + b, 0) / metrics.length);
  }, [marketStats, targetThresholds]);

  // Get position label
  const getPositionLabel = (pos) => {
    if (pos === 1) return { label: 'Market Leader', icon: FaCrown, color: 'yellow.500' };
    if (pos <= 3) return { label: 'Top Tier', icon: FaTrophy, color: 'orange.500' };
    if (pos <= 5) return { label: 'Strong Position', icon: FaMedal, color: 'blue.500' };
    if (pos <= 10) return { label: 'Competitive', icon: FaChartLine, color: 'green.500' };
    return { label: 'Growth Target', icon: FaChartBar, color: 'purple.500' };
  };

  const positionInfo = getPositionLabel(targetPosition);

  if (isLoading) {
    return (
      <Box
        bg={cardBg}
        borderRadius="2xl"
        p={8}
        h="100%"
        minH="480px"
        boxShadow="xl"
        border="1px solid"
        borderColor={borderColor}
      >
        <Flex justify="center" align="center" h="100%">
          <VStack spacing={4}>
            <Spinner size="xl" color="purple.500" thickness="4px" />
            <Text color={subtitleColor} fontSize="sm">Analyzing market goals...</Text>
          </VStack>
        </Flex>
      </Box>
    );
  }

  return (
    <MotionBox
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      bg={cardBg}
      borderRadius="2xl"
      overflow="hidden"
      h="100%"
      minH="480px"
      boxShadow="xl"
      border="1px solid"
      borderColor={borderColor}
      position="relative"
      _hover={{
        boxShadow: `0 25px 50px -12px ${glowColor}`,
        borderColor: hoverBorderColor,
      }}
      sx={{
        transition: 'all 0.3s ease',
      }}
    >
      {/* Header - Matching Ad Intelligence Style */}
      <Box
        px={5}
        py={4}
        borderBottomWidth="1px"
        borderColor={borderColor}
        bg={headerBg}
      >
        <Flex justify="space-between" align="center" flexWrap="wrap" gap={3}>
          <HStack spacing={3}>
            <Flex
              w="44px"
              h="44px"
              bg={iconBg}
              borderRadius="lg"
              justify="center"
              align="center"
            >
              <Icon as={FaBullseye} color="purple.500" boxSize={5} />
            </Flex>
            <VStack align="start" spacing={0}>
              <Text fontWeight="bold" fontSize="1.4em" color={textColor} textTransform="uppercase" fontStyle="italic">
                Goals
              </Text>
              <Text fontSize="xs" color={subtitleColor}>
                Market Performance Targets • {marketStats?.totalBusinesses || 0} businesses
              </Text>
            </VStack>
          </HStack>
          <HStack spacing={2}>
            <Badge
              colorScheme="purple"
              fontSize="sm"
              px={3}
              py={1.5}
              borderRadius="full"
              fontWeight="bold"
            >
              <HStack spacing={1}>
                <Icon as={positionInfo.icon} boxSize={3} />
                <Text>Top {targetPosition}</Text>
              </HStack>
            </Badge>
            <Button
              size="md"
              colorScheme="blue"
              variant="outline"
              rightIcon={<FaChevronRight />}
              onClick={handleViewGoals}
            >
              Full Dashboard
            </Button>
          </HStack>
        </Flex>
      </Box>

      <VStack spacing={0} flex="1">
        {/* Interactive Slider Section */}
        <Box w="100%" px={5} py={4} bg={metricBg}>
          <Flex justify="space-between" align="center" mb={3}>
            <HStack spacing={2}>
              <Icon as={positionInfo.icon} color={positionInfo.color} boxSize={4} />
              <Text fontSize="sm" fontWeight="600" color={textColor}>
                {positionInfo.label}
              </Text>
            </HStack>
            <Text fontSize="xs" color={subtitleColor}>
              {marketStats?.totalBusinesses || 0} businesses in market
            </Text>
          </Flex>
          
          <Slider
            min={0}
            max={SLIDER_POSITIONS.length - 1}
            step={1}
            value={sliderIndex}
            onChange={handleSliderChange}
          >
            <SliderTrack 
              bg={sliderTrackBg} 
              h="10px" 
              borderRadius="full"
            >
              <SliderFilledTrack 
                bgGradient="linear(to-r, purple.400, blue.400, cyan.400)"
                borderRadius="full"
              />
            </SliderTrack>
            <SliderThumb
              boxSize={6}
              bg="white"
              border="3px solid"
              borderColor="purple.500"
              boxShadow="lg"
              _focus={{ boxShadow: '0 0 0 4px rgba(128, 90, 213, 0.3)' }}
            >
              <Box fontSize="xs" fontWeight="bold" color="purple.500">
                {targetPosition}
              </Box>
            </SliderThumb>
          </Slider>
          
          {/* Slider Labels */}
          <HStack justify="space-between" mt={2}>
            {SLIDER_POSITIONS.map((pos) => (
              <Text
                key={pos}
                fontSize="xs"
                fontWeight={targetPosition === pos ? 'bold' : 'medium'}
                color={targetPosition === pos ? 'purple.500' : subtitleColor}
                cursor="pointer"
                onClick={() => setTargetPosition(pos)}
                _hover={{ color: 'purple.500' }}
                transition="all 0.15s"
                px={1}
              >
                #{pos}
              </Text>
            ))}
          </HStack>
        </Box>

        {/* Main Metrics Grid */}
        <Box flex="1" w="100%" px={5} py={4} overflowY="auto">
          <Grid templateColumns={{ base: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }} gap={3}>
            {/* Total Reviews Metric */}
            <GridItem colSpan={1}>
              <MotionBox
                whileHover={{ scale: 1.02 }}
                bg={hoveredMetric === 'reviews' ? metricHoverBg : metricBg}
                p={4}
                borderRadius="xl"
                border="1px solid"
                borderColor={hoveredMetric === 'reviews' ? 'blue.200' : 'transparent'}
                cursor="pointer"
                onMouseEnter={() => setHoveredMetric('reviews')}
                onMouseLeave={() => setHoveredMetric(null)}
                transition="all 0.2s"
              >
                <HStack justify="space-between" mb={2}>
                  <HStack spacing={2}>
                    <Flex
                      w="32px"
                      h="32px"
                      borderRadius="lg"
                      bgGradient="linear(135deg, orange.400, yellow.400)"
                      justify="center"
                      align="center"
                    >
                      <Icon as={FaStar} color="white" boxSize={4} />
                    </Flex>
                    <VStack align="start" spacing={0}>
                      <Text fontSize="xs" color={subtitleColor} fontWeight="medium">
                        Total Reviews
                      </Text>
                      <Text fontSize="lg" fontWeight="bold" color={textColor}>
                        {targetThresholds?.totalReviews?.toLocaleString() || '—'}
                      </Text>
                    </VStack>
                  </HStack>
                </HStack>
                
                <Tooltip label={`Market leader: ${targetThresholds?.topReviewsBusiness}`} placement="top">
                  <Box>
                    <HStack justify="space-between" fontSize="2xs" color={subtitleColor} mb={1}>
                      <Text>Market avg: {marketStats?.avgReviews?.toLocaleString() || 0}</Text>
                      <Text>Leader: {targetThresholds?.marketLeader?.toLocaleString() || 0}</Text>
                    </HStack>
                    <Progress 
                      value={(marketStats?.avgReviews / (targetThresholds?.totalReviews || 1)) * 100}
                      size="sm" 
                      colorScheme="orange"
                      borderRadius="full"
                      bg={sliderTrackBg}
                    />
                  </Box>
                </Tooltip>
              </MotionBox>
            </GridItem>

            {/* Monthly Growth Metric */}
            <GridItem colSpan={1}>
              <MotionBox
                whileHover={{ scale: 1.02 }}
                bg={hoveredMetric === 'monthly' ? metricHoverBg : metricBg}
                p={4}
                borderRadius="xl"
                border="1px solid"
                borderColor={hoveredMetric === 'monthly' ? 'green.200' : 'transparent'}
                cursor="pointer"
                onMouseEnter={() => setHoveredMetric('monthly')}
                onMouseLeave={() => setHoveredMetric(null)}
                transition="all 0.2s"
              >
                <HStack justify="space-between" mb={2}>
                  <HStack spacing={2}>
                    <Flex
                      w="32px"
                      h="32px"
                      borderRadius="lg"
                      bgGradient="linear(135deg, green.400, teal.400)"
                      justify="center"
                      align="center"
                    >
                      <Icon as={FaChartLine} color="white" boxSize={4} />
                    </Flex>
                    <VStack align="start" spacing={0}>
                      <Text fontSize="xs" color={subtitleColor} fontWeight="medium">
                        Monthly Velocity
                      </Text>
                      <HStack spacing={1} align="baseline">
                        <Text fontSize="lg" fontWeight="bold" color={textColor}>
                          {targetThresholds?.monthlyReviews || '—'}
                        </Text>
                        <Text fontSize="xs" color={subtitleColor}>/mo</Text>
                      </HStack>
                    </VStack>
                  </HStack>
                </HStack>
                
                <Tooltip label={`Fastest growing: ${targetThresholds?.topMonthlyBusiness}`} placement="top">
                  <Box>
                    <HStack justify="space-between" fontSize="2xs" color={subtitleColor} mb={1}>
                      <Text>Market avg: {marketStats?.avgMonthly || 0}/mo</Text>
                      <Text>Leader: {targetThresholds?.monthlyLeader?.toFixed(1) || 0}/mo</Text>
                    </HStack>
                    <Progress 
                      value={(marketStats?.avgMonthly / (targetThresholds?.monthlyReviews || 1)) * 100}
                      size="sm" 
                      colorScheme="green"
                      borderRadius="full"
                      bg={sliderTrackBg}
                    />
                  </Box>
                </Tooltip>
              </MotionBox>
            </GridItem>

            {/* Sentiment Score Metric */}
            <GridItem colSpan={1}>
              <MotionBox
                whileHover={{ scale: 1.02 }}
                bg={hoveredMetric === 'sentiment' ? metricHoverBg : metricBg}
                p={4}
                borderRadius="xl"
                border="1px solid"
                borderColor={hoveredMetric === 'sentiment' ? 'blue.200' : 'transparent'}
                cursor="pointer"
                onMouseEnter={() => setHoveredMetric('sentiment')}
                onMouseLeave={() => setHoveredMetric(null)}
                transition="all 0.2s"
              >
                <HStack justify="space-between" mb={2}>
                  <HStack spacing={2}>
                    <Flex
                      w="32px"
                      h="32px"
                      borderRadius="lg"
                      bgGradient="linear(135deg, blue.400, purple.400)"
                      justify="center"
                      align="center"
                    >
                      <Icon as={FaSmile} color="white" boxSize={4} />
                    </Flex>
                    <VStack align="start" spacing={0}>
                      <Text fontSize="xs" color={subtitleColor} fontWeight="medium">
                        Sentiment Score
                      </Text>
                      <HStack spacing={1} align="baseline">
                        <Text fontSize="lg" fontWeight="bold" color={textColor}>
                          {targetThresholds?.sentimentScore || '—'}%
                        </Text>
                      </HStack>
                    </VStack>
                  </HStack>
                </HStack>
                
                <Tooltip label={`Best rated: ${targetThresholds?.topSentimentBusiness}`} placement="top">
                  <Box>
                    <HStack justify="space-between" fontSize="2xs" color={subtitleColor} mb={1}>
                      <Text>Market avg: {marketStats?.avgSentiment || 0}%</Text>
                      <Text>Leader: {targetThresholds?.sentimentLeader?.toFixed(0) || 0}%</Text>
                    </HStack>
                    <Progress 
                      value={(marketStats?.avgSentiment / (targetThresholds?.sentimentScore || 1)) * 100}
                      size="sm" 
                      colorScheme="purple"
                      borderRadius="full"
                      bg={sliderTrackBg}
                    />
                  </Box>
                </Tooltip>
              </MotionBox>
            </GridItem>

            {/* Website Traffic Metric */}
            <GridItem colSpan={1}>
              <MotionBox
                whileHover={{ scale: 1.02 }}
                bg={hoveredMetric === 'traffic' ? metricHoverBg : metricBg}
                p={4}
                borderRadius="xl"
                border="1px solid"
                borderColor={hoveredMetric === 'traffic' ? 'purple.200' : 'transparent'}
                cursor="pointer"
                onMouseEnter={() => setHoveredMetric('traffic')}
                onMouseLeave={() => setHoveredMetric(null)}
                transition="all 0.2s"
              >
                <HStack justify="space-between" mb={2}>
                  <HStack spacing={2}>
                    <Flex
                      w="32px"
                      h="32px"
                      borderRadius="lg"
                      bgGradient="linear(135deg, purple.400, pink.400)"
                      justify="center"
                      align="center"
                    >
                      <Icon as={FaGlobe} color="white" boxSize={4} />
                    </Flex>
                    <VStack align="start" spacing={0}>
                      <Text fontSize="xs" color={subtitleColor} fontWeight="medium">
                        Website Traffic
                      </Text>
                      <HStack spacing={1} align="baseline">
                        <Text fontSize="lg" fontWeight="bold" color={textColor}>
                          {targetThresholds?.trafficTarget > 0 
                            ? formatNumber(targetThresholds.trafficTarget)
                            : '—'}
                        </Text>
                        {targetThresholds?.trafficTarget > 0 && (
                          <Text fontSize="xs" color={subtitleColor}>/mo</Text>
                        )}
                      </HStack>
                    </VStack>
                  </HStack>
                </HStack>
                
                <Tooltip label={`Top traffic: ${targetThresholds?.topTrafficBusiness}`} placement="top">
                  <Box>
                    <HStack justify="space-between" fontSize="2xs" color={subtitleColor} mb={1}>
                      <Text>Avg: {marketStats?.avgTraffic > 0 ? formatNumber(marketStats.avgTraffic) : '—'}/mo</Text>
                      <Text>Leader: {targetThresholds?.trafficLeader > 0 ? formatNumber(targetThresholds.trafficLeader) : '—'}/mo</Text>
                    </HStack>
                    <Progress 
                      value={targetThresholds?.trafficTarget > 0 
                        ? (marketStats?.avgTraffic / targetThresholds.trafficTarget) * 100 
                        : 0}
                      size="sm" 
                      colorScheme="pink"
                      borderRadius="full"
                      bg={sliderTrackBg}
                    />
                  </Box>
                </Tooltip>
              </MotionBox>
            </GridItem>

            {/* SEO Score Metric */}
            <GridItem colSpan={1}>
              <MotionBox
                whileHover={{ scale: 1.02 }}
                bg={hoveredMetric === 'seo' ? metricHoverBg : metricBg}
                p={4}
                borderRadius="xl"
                border="1px solid"
                borderColor={hoveredMetric === 'seo' ? 'teal.200' : 'transparent'}
                cursor="pointer"
                onMouseEnter={() => setHoveredMetric('seo')}
                onMouseLeave={() => setHoveredMetric(null)}
                transition="all 0.2s"
              >
                <HStack justify="space-between" mb={2}>
                  <HStack spacing={2}>
                    <Flex
                      w="32px"
                      h="32px"
                      borderRadius="lg"
                      bgGradient="linear(135deg, teal.400, cyan.400)"
                      justify="center"
                      align="center"
                    >
                      <Icon as={FaSearch} color="white" boxSize={4} />
                    </Flex>
                    <VStack align="start" spacing={0}>
                      <Text fontSize="xs" color={subtitleColor} fontWeight="medium">
                        SEO Score
                      </Text>
                      <HStack spacing={1} align="baseline">
                        <Text fontSize="lg" fontWeight="bold" color={textColor}>
                          {targetThresholds?.seoScoreTarget > 0 
                            ? targetThresholds.seoScoreTarget.toLocaleString()
                            : '—'}
                        </Text>
                      </HStack>
                    </VStack>
                  </HStack>
                </HStack>
                
                <Tooltip label={`Top SEO: ${targetThresholds?.topSeoBusiness}`} placement="top">
                  <Box>
                    <HStack justify="space-between" fontSize="2xs" color={subtitleColor} mb={1}>
                      <Text>Avg: {marketStats?.avgSeoScore > 0 ? marketStats.avgSeoScore.toLocaleString() : '—'}</Text>
                      <Text>Leader: {targetThresholds?.seoScoreLeader > 0 ? Math.round(targetThresholds.seoScoreLeader).toLocaleString() : '—'}</Text>
                    </HStack>
                    <Progress 
                      value={targetThresholds?.seoScoreTarget > 0 
                        ? (marketStats?.avgSeoScore / targetThresholds.seoScoreTarget) * 100 
                        : 0}
                      size="sm" 
                      colorScheme="teal"
                      borderRadius="full"
                      bg={sliderTrackBg}
                    />
                  </Box>
                </Tooltip>
              </MotionBox>
            </GridItem>

            {/* Overall Progress Ring */}
            <GridItem colSpan={1}>
              <MotionBox
                whileHover={{ scale: 1.02 }}
                bg={metricBg}
                p={4}
                borderRadius="xl"
                h="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
              >
                <Text fontSize="xs" color={subtitleColor} fontWeight="medium" mb={2}>
                  Goal Progress
                </Text>
                <CircularProgress 
                  value={overallProgress} 
                  size="80px"
                  thickness="10px"
                  color={overallProgress >= 80 ? 'green.400' : overallProgress >= 50 ? 'yellow.400' : 'orange.400'}
                  trackColor={sliderTrackBg}
                >
                  <CircularProgressLabel>
                    <VStack spacing={0}>
                      <Text fontSize="lg" fontWeight="bold" color={textColor}>
                        {overallProgress}%
                      </Text>
                    </VStack>
                  </CircularProgressLabel>
                </CircularProgress>
                <HStack mt={2} fontSize="2xs" color={subtitleColor}>
                  <Icon as={overallProgress >= 50 ? FaArrowUp : FaArrowDown} color={overallProgress >= 50 ? 'green.500' : 'red.500'} />
                  <Text>{overallProgress >= 50 ? 'On track' : 'Below target'}</Text>
                </HStack>
              </MotionBox>
            </GridItem>
          </Grid>

          {/* Market Trend Sparkline */}
          {trendData.length > 0 && (
            <Box mt={4} bg={metricBg} p={4} borderRadius="xl">
              <HStack justify="space-between" mb={2}>
                <HStack spacing={2}>
                  <Icon as={FaFireAlt} color="orange.400" boxSize={3} />
                  <Text fontSize="xs" fontWeight="600" color={textColor}>
                    Market Activity Trend
                  </Text>
                </HStack>
                <Badge colorScheme="green" fontSize="2xs" variant="subtle">
                  <HStack spacing={1}>
                    <Icon as={FaArrowUp} boxSize={2} />
                    <Text>6 months</Text>
                  </HStack>
                </Badge>
              </HStack>
              <Box h="50px">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={trendData}>
                    <defs>
                      <linearGradient id="colorTrend" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#805AD5" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#805AD5" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <Area 
                      type="monotone" 
                      dataKey="value" 
                      stroke="#805AD5" 
                      strokeWidth={2}
                      fill="url(#colorTrend)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          )}
        </Box>

        {/* Bottom CTA Section - Matching Ad Intelligence Style */}
        <Box
          w="100%"
          px={5}
          py={4}
          bg={ctaBg}
          borderTopWidth="1px"
          borderColor={borderColor}
        >
          <Flex justify="space-between" align="center" flexWrap="wrap" gap={3}>
            <VStack align="start" spacing={0}>
              <HStack spacing={2}>
                <Icon as={FaCheckCircle} color="purple.500" boxSize={4} />
                <Text fontWeight="bold" color={ctaTextColor}>
                  Goal Setting
                </Text>
              </HStack>
              <Text fontSize="sm" color={subtitleColor}>
                Set custom targets to track your market position
              </Text>
            </VStack>
            <Button
              colorScheme="purple"
              size="lg"
              rightIcon={<FaChevronRight />}
              onClick={handleViewGoals}
              shadow="md"
              _hover={{ transform: 'translateY(-2px)', shadow: 'lg' }}
            >
              Set Your Goals
            </Button>
          </Flex>
        </Box>
      </VStack>
    </MotionBox>
  );
};

export default GoalsWidget;
